import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { usePlayer } from '../hooks/usePlayer.js'
import { getMyClubs, answerClubInvite, setCoachAccess, markClubNoticeSeen } from '../api/levelApi.js'

// Rutas en las que no se enseña nada (sin sesión de verdad, o a medio alta)
const SKIP = ['/login', '/waitlist', '/confirmar', '/onboarding', '/admin', '/grupo/unirse', '/reto']

/**
 * Lo que los clubes tienen que contarle al jugador al abrir la app
 * (engine/club_members.py), una cosa tras otra: invitaciones pendientes a su
 * correo (aceptar / rechazar) y, la primera vez en cada club, el aviso de que
 * sus profesores ven sus vídeos y análisis. El permiso viene activado
 * (Guillermo, 30/10/2026): "Entendido" lo deja así; "Desactivar" lo quita, y
 * también se puede cambiar luego en Perfil › Mis clubes.
 *
 * Va por debajo de LegalGate (z-index): primero se aceptan los términos.
 * Si la consulta falla no se bloquea nada; se vuelve a mirar en la siguiente carga.
 */
export default function ClubGate() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { session, passwordRecovery } = usePlayer()
  const userId = session?.user?.id
  const [queue, setQueue] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // Se vuelve a mirar al volver a la app (p. ej. tras aceptar una invitación en otro dispositivo)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const onShow = () => { if (document.visibilityState === 'visible') setTick(n => n + 1) }
    document.addEventListener('visibilitychange', onShow)
    return () => document.removeEventListener('visibilitychange', onShow)
  }, [])

  useEffect(() => {
    if (!userId) { setQueue([]); return }
    let cancelled = false
    getMyClubs()
      .then(r => {
        if (cancelled) return
        setQueue([
          ...(r?.invites || []).map(i => ({ kind: 'invite', ...i })),
          ...(r?.clubs || []).filter(c => c.status === 'active' && !c.notice_seen)
            .map(c => ({ kind: 'notice', club_id: c.id, club_name: c.name })),
        ])
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [userId, tick])

  const item = queue[0]
  if (!item || passwordRecovery || SKIP.some(p => pathname.startsWith(p))) return null

  async function answer(yes) {
    setBusy(true)
    setError('')
    try {
      if (item.kind === 'invite') {
        await answerClubInvite(item.id, yes)
        // Al aceptar, el club ya es suyo: toca el aviso de los profesores
        setQueue(q => yes
          ? [{ kind: 'notice', club_id: item.club_id, club_name: item.club_name }, ...q.slice(1)]
          : q.slice(1))
      } else {
        // "Entendido" deja el permiso como está; "Desactivar" lo quita
        await (yes ? markClubNoticeSeen(item.club_id) : setCoachAccess(item.club_id, false))
        setQueue(q => q.slice(1))
      }
    } catch {
      setError(t('clubs.err.generic'))
    } finally {
      setBusy(false)
    }
  }

  const role = item.as_admin && item.as_teacher ? t('clubs.roleBoth')
    : item.as_admin ? t('clubs.roleAdmin').toLowerCase()
    : item.as_teacher ? t('clubs.roleTeacher').toLowerCase() : ''

  return (
    <div className="legal-gate" style={{ zIndex: 9990 }} role="dialog" aria-modal="true" aria-labelledby="club-gate-title">
      <div className="legal-gate-card">
        {item.kind === 'invite' ? (
          <h2 id="club-gate-title">
            {role ? t('clubs.inviteFromRole', { club: item.club_name, role }) : t('clubs.inviteFrom', { club: item.club_name })}
          </h2>
        ) : (
          <>
            <h2 id="club-gate-title">{t('clubs.noticeTitle', { club: item.club_name })}</h2>
            <p>{t('clubs.noticeBody')}</p>
          </>
        )}
        {error ? <p className="error-text">{error}</p> : null}
        <button className="button-primary" type="button" disabled={busy} onClick={() => answer(true)}>
          {item.kind === 'invite' ? t('clubs.accept') : t('clubs.noticeOk')}
        </button>
        <button className="button-secondary" type="button" disabled={busy} onClick={() => answer(false)}
          style={{ width: '100%', padding: '11px 14px' }}>
          {item.kind === 'invite' ? t('clubs.decline') : t('clubs.noticeOff')}
        </button>
      </div>
    </div>
  )
}
